const UNSUPPORTED_SEARCH_STATUS_CODES = new Set([400, 404, 405, 422]);

/**
 * A successful empty response means the server understood the search and
 * found nothing. Only an explicit client/validation error about the search
 * parameter is safe to treat as an unsupported-search signal.
 */
export const isUnsupportedSearchError = (error) => {
  const status = error?.response?.status;
  if (!UNSUPPORTED_SEARCH_STATUS_CODES.has(status)) return false;

  const data = error?.response?.data;
  const message = [
    data?.message,
    data?.error,
    ...(Array.isArray(data?.errors?.search) ? data.errors.search : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /search|filter|query|parameter|unknown field|not allowed|not supported/.test(
    message,
  );
};

const explicitlyRejectedSearch = (response) => {
  const payload = response?.data || {};
  const candidates = [
    payload.search_applied,
    payload?.data?.search_applied,
    payload?.meta?.search_applied,
    payload?.data?.meta?.search_applied,
  ];

  return candidates.includes(false);
};

export const fetchWithSearchFallback = async ({
  search,
  serverSearchAvailable,
  fetchPage,
}) => {
  const normalizedSearch = search.trim();

  if (!normalizedSearch || serverSearchAvailable === false) {
    return {
      response: await fetchPage({ search: "" }),
      usedServerSearch: false,
      serverSearchAvailable,
    };
  }

  try {
    const response = await fetchPage({ search: normalizedSearch });

    // APIs that silently accept unknown query parameters can opt into this
    // explicit capability signal. An empty result is deliberately not treated
    // as a rejection: it is a valid successful search with no matches.
    if (explicitlyRejectedSearch(response)) {
      return {
        response: await fetchPage({ search: "" }),
        usedServerSearch: false,
        serverSearchAvailable: false,
      };
    }

    return {
      response,
      usedServerSearch: true,
      serverSearchAvailable: true,
    };
  } catch (error) {
    if (!isUnsupportedSearchError(error)) throw error;

    return {
      response: await fetchPage({ search: "" }),
      usedServerSearch: false,
      serverSearchAvailable: false,
    };
  }
};
